<template>
  <article class="sf-card">
    <a :href="card.url" class="sf-card__link">
      <!--
        The media area always keeps its 4:3 shape. Without an image it falls
        back to a taupe area with the category icon, or - since only a handful
        of categories have an icon - the brand landscape shape.
      -->
      <div class="sf-card__media">
        <img
          v-if="card.image"
          :src="card.image"
          :alt="card.title"
          class="sf-card__image"
          loading="lazy"
        />
        <div v-else class="sf-card__placeholder">
          <img
            v-if="card.categoryIcon"
            :src="card.categoryIcon"
            class="sf-card__placeholder-icon"
            alt=""
            aria-hidden="true"
          />
          <ef2-teaser-shape v-else class="sf-card__placeholder-shape" />
        </div>

        <div v-if="card.date" class="sf-card__date" aria-hidden="true">
          <span class="sf-card__date-day">{{ card.date.day }}</span>
          <span class="sf-card__date-month">{{ card.date.month }}</span>
        </div>
      </div>

      <div class="sf-card__body">
        <span v-if="card.category" class="sf-card__category">{{ card.category }}</span>
        <h3 class="sf-card__title">{{ card.title }}</h3>
        <!--
          Always rendered, also when empty: it reserves the line so every card
          in a row ends up the same height. The date of an event is not repeated
          here, it already sits on the image as a badge.
        -->
        <span class="sf-card__meta">{{ card.city }}</span>
      </div>
    </a>

    <div class="sf-card__favorite">
      <ef2-favorite-button :entity-id="card.nid" />
    </div>
  </article>
</template>

<script>
export default {
  name: 'StoryfinderCard',

  props: {
    card: { type: Object, required: true },
  },
};
</script>

<style lang="scss" scoped>
$sf-taupe: #e8ddc9;
$sf-black: #23201e;
$sf-yellow: #e5bb5f;

.sf-card {
  position: relative;
  flex-shrink: 0;
  scroll-snap-align: start;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &__link {
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: inherit;
    height: 100%;

    &:focus-visible {
      outline: 2px solid #517b86;
      outline-offset: 2px;
    }
  }

  // ── Media: always a 4:3 area, image or placeholder ────────────────────────

  // The padding trick instead of aspect-ratio: this theme still compiles for
  // older browsers, and the image area has to hold its 4:3 shape even when
  // there is no image at all.
  &__media {
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 75%;
    background-color: $sf-taupe;
    overflow: hidden;
  }

  &__image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $sf-taupe;
  }

  &__placeholder-icon {
    width: 40%;
    max-width: 56px;
    height: auto;
    opacity: 0.45;
  }

  // The brand landscape shape, anchored to the bottom of the image area.
  &__placeholder-shape {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: auto;
    display: block;
    opacity: 0.35;
  }

  // ── Date badge, only for events ───────────────────────────────────────────

  &__date {
    position: absolute;
    top: 6px;
    left: 6px;
    background: $sf-yellow;
    border-radius: 6px;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1;
    min-width: 32px;
  }

  &__date-day {
    font-size: 15px;
    font-weight: 700;
    color: $sf-black;
  }

  &__date-month {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba($sf-black, 0.7);
    margin-top: 1px;
  }

  // ── Body ──────────────────────────────────────────────────────────────────

  &__body {
    padding: 10px 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
  }

  &__category {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba($sf-black, 0.6);

    // One line, so a long category label cannot push the title down.
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__title {
    font-family: 'Comfortaa', sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.25;
    color: $sf-black;
    margin: 0;

    // Exactly two lines: min-height keeps single-line titles aligned with
    // two-line ones, line-clamp truncates anything longer.
    min-height: calc(2 * 1.25 * 14px);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__meta {
    font-size: 12px;
    line-height: 1.3;
    color: rgba($sf-black, 0.65);

    // Reserve the line even when empty, so cards stay the same height.
    min-height: calc(1.3 * 12px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // ── Favorite button ───────────────────────────────────────────────────────

  &__favorite {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 1;
  }
}
</style>
